/**
 * Transport bar for the replay route: prev / play-pause / next buttons,
 * speed selector, current step / total counter. Keyboard bindings live
 * in HandReplayRoute via useKeyboardShortcuts; this component is pure
 * presentational.
 */
import { SPEED_OPTIONS, type Speed } from '@/hooks/useReviewPlayer';

export type ReplayControlsProps = {
  step: number;
  totalSteps: number;
  playing: boolean;
  speed: Speed;
  onPrev: () => void;
  onNext: () => void;
  onToggle: () => void;
  onSpeedChange: (speed: Speed) => void;
};

export function ReplayControls({
  step,
  totalSteps,
  playing,
  speed,
  onPrev,
  onNext,
  onToggle,
  onSpeedChange,
}: ReplayControlsProps) {
  const atStart = step <= 0;
  const atEnd = step >= Math.max(0, totalSteps - 1);
  return (
    <div
      className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-bg-elev"
      data-testid="replay-controls"
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={atStart}
          className="w-8 h-8 rounded-md bg-bg border border-border text-sm disabled:opacity-30"
          aria-label="이전 스텝"
          data-testid="replay-prev"
        >
          ◀
        </button>
        <button
          type="button"
          onClick={onToggle}
          disabled={atEnd && !playing}
          className="w-8 h-8 rounded-md bg-accent text-bg text-sm font-semibold disabled:opacity-30"
          aria-label={playing ? '일시정지' : '재생'}
          data-testid="replay-toggle"
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={atEnd}
          className="w-8 h-8 rounded-md bg-bg border border-border text-sm disabled:opacity-30"
          aria-label="다음 스텝"
          data-testid="replay-next"
        >
          ▶|
        </button>
      </div>
      <div className="text-xs font-mono tabular-nums text-muted" data-testid="replay-step">
        {step + 1} / {totalSteps}
      </div>
      <div className="flex items-center gap-1" data-testid="replay-speed-row">
        {SPEED_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onSpeedChange(s)}
            className={`text-[10px] font-mono rounded-md px-2 py-1 border ${
              speed === s
                ? 'bg-accent text-bg border-accent'
                : 'bg-bg border-border text-muted hover:text-fg'
            }`}
            data-testid={`replay-speed-${s}`}
          >
            {s}×
          </button>
        ))}
      </div>
    </div>
  );
}
